import { useQuery } from "@tanstack/react-query";
import { api, FESTIVAL_META } from "@/lib/api";
import { Link, useParams } from "react-router-dom";
import { User, Heart, Lock, ArrowLeft, Music, Play } from "lucide-react";
import { useState } from "react";
import AchievementBadge from "@/components/AchievementBadge";

function SlotRow({ slot, artist, isRevealed }) {
  const [open, setOpen] = useState(false);
  const dotCls = slot.status === "hit" ? "dot dot-hit" : slot.status === "miss" ? "dot dot-miss" : "dot dot-pending";
  return (
    <>
      <div className="px-5 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {isRevealed && <span className={dotCls} />}
          {artist?.image_url ? (
            <img src={artist.image_url} className="w-8 h-8 rounded object-cover flex-shrink-0" alt="" />
          ) : (
            <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0"><Music size={12} className="text-zinc-600" /></div>
          )}
          <div className="min-w-0">
            <div className="font-medium truncate">{slot.artist_name}</div>
            <div className="text-xs text-zinc-500 capitalize truncate">{slot.chosen_category}</div>
          </div>
        </div>
        {artist?.spotify_id && (
          <button onClick={() => setOpen(v => !v)} className={`p-1.5 rounded ${open ? "text-emerald-400" : "text-zinc-500"} hover:bg-white/5`}><Play size={14} /></button>
        )}
        {isRevealed && slot.status === "hit" && <span className="text-emerald-400 text-xs font-semibold">+{slot.points}</span>}
      </div>
      {open && artist?.spotify_id && (
        <iframe src={`https://open.spotify.com/embed/artist/${artist.spotify_id}?theme=0`} width="100%" height="152" frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title={artist.name} className="border-b border-white/5" />
      )}
    </>
  );
}

export default function UserProfile() {
  const { userId } = useParams();
  const { data: profile } = useQuery({
    queryKey: ["user-profile", userId],
    queryFn: () => api.get(`/users/${userId}/profile`).then(r => r.data),
    retry: 0,
  });
  const { data: festData } = useQuery({ queryKey: ["festivals"], queryFn: () => api.get("/festivals").then(r => r.data) });

  if (!profile) return <div className="max-w-4xl mx-auto p-8 text-zinc-400">Indlæser…</div>;

  const artistsMap = profile.artists || {};

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <Link to="/brugere" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6" data-testid="user-back">
        <ArrowLeft size={14} /> Alle brugere
      </Link>

      <div className="card-flat p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          {profile.effective_picture ? (
            <img src={profile.effective_picture} alt="" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover border border-white/10" onError={e => e.currentTarget.style.display="none"} data-testid="user-profile-picture" />
          ) : (
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-zinc-800 flex items-center justify-center"><User size={36} className="text-zinc-500" /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow">Spillerprofil</div>
            <h1 className="hero-title text-4xl sm:text-5xl mt-1" data-testid="user-profile-name">{profile.display_name}</h1>
            {profile.bio && <p className="mt-3 text-zinc-400" data-testid="user-profile-bio">{profile.bio}</p>}
            <div className="mt-5 flex flex-wrap items-baseline gap-6">
              <div>
                <div className="label-eyebrow">Point</div>
                <div className="font-score text-4xl">{profile.total}</div>
              </div>
              <div>
                <div className="label-eyebrow">Placering</div>
                <div className="font-score text-4xl">#{profile.position ?? "–"}</div>
              </div>
            </div>
          </div>
        </div>

        {(profile.top_artist_wishes?.length > 0 || profile.top_artist_wish) && (
          <div className="mt-8 pt-6 border-t border-white/5">
            <div className="flex items-center gap-2 mb-3">
              <Heart size={14} className="text-pink-400" />
              <div className="label-eyebrow">Topartist-ønsker</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {(profile.top_artist_wishes?.length ? profile.top_artist_wishes : [profile.top_artist_wish]).map((w, i) => (
                <div key={w?.artist_id || i} className="card-flat p-4 flex items-center gap-4" data-testid={`user-profile-wish-${i}`}>
                  <div className="font-score text-lg text-zinc-500 w-5 text-center">{i + 1}</div>
                  {w?.image_url ? (
                    <img src={w.image_url} className="w-12 h-12 rounded-md object-cover" alt="" />
                  ) : <div className="w-12 h-12 rounded-md bg-zinc-800 flex items-center justify-center"><Music size={14} /></div>}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{w?.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">Drømmer om at se live</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {profile.achievements?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-white/5" data-testid="user-profile-achievements">
            <div className="label-eyebrow mb-3">Achievements ({profile.achievements.length})</div>
            <div className="flex flex-wrap gap-3">
              {profile.achievements.map(a => (
                <AchievementBadge key={a.achievement_id}
                  id={a.achievement_id} name={a.name} icon={a.icon} description={a.description}
                  unlockedAt={a.unlocked_at} size="md"
                  testId={`ach-${a.achievement_id}`} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Predictions */}
      <div className="mt-10">
        <h2 className="font-display font-bold text-2xl mb-4">
          {profile.is_self ? "Dine forudsigelser" : `${profile.display_name}s forudsigelser`}
        </h2>
        {!profile.predictions_revealed ? (
          <div className="card-flat p-10 text-center" data-testid="user-profile-locked">
            <Lock size={24} className="mx-auto text-zinc-500 mb-3" />
            <div className="font-medium">Forudsigelser er skjulte indtil deadline</div>
            <div className="text-sm text-zinc-500 mt-2">Alle brugeres line-ups afsløres når deadline er passeret.</div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="user-profile-predictions">
            {festData?.festivals.map(f => {
              const meta = FESTIVAL_META[f.id];
              const s = profile.festival_scores?.[f.id];
              const slots = s?.slots || [];
              const hasAny = slots.length > 0 || s?.orange_slot;
              return (
                <div key={f.id} className="card-flat overflow-hidden">
                  <div className="px-5 py-4 flex items-center justify-between" style={{ borderLeft: `3px solid ${meta.color}` }}>
                    <div>
                      <div className="font-display font-bold text-xl">{f.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{slots.length} valg · {s?.hits ?? 0} hits</div>
                    </div>
                    <div className="text-right">
                      <div className="font-score text-2xl">{s?.points ?? 0}</div>
                      <div className="text-xs text-zinc-500">/ {s?.max_points}</div>
                    </div>
                  </div>
                  {hasAny ? (
                    <div className="border-t border-white/5 divide-y divide-white/5">
                      {slots.map((slot, i) => <SlotRow key={i} slot={slot} artist={artistsMap[slot.artist_id]} isRevealed={profile.is_locked} />)}
                      {s?.orange_slot && <SlotRow slot={s.orange_slot} artist={artistsMap[s.orange_slot.artist_id]} isRevealed={profile.is_locked} />}
                    </div>
                  ) : (
                    <div className="px-5 py-4 text-zinc-500 text-sm border-t border-white/5">Ingen valg endnu.</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
